import { Reveal } from "@/components/Reveal";
import { ArrowRight } from "lucide-react";

const mapping = [
  { current: "Home / Landing", proposed: "Home", note: "Cinematic flagship experience" },
  { current: "Our Menu (Dinner / Checkle)", proposed: "Menu → Dinner", note: "Native HTML menu, no PDF/embed" },
  { current: "Brunch Buffet", proposed: "Menu → Brunch", note: "Consolidated under Menu" },
  { current: "Bar Bites", proposed: "Menu → Bar Bites", note: "" },
  { current: "Desserts", proposed: "Menu → Desserts", note: "" },
  { current: "Drinks / Libations", proposed: "Menu → Wine & Cocktails", note: "Merged libations + wine list" },
  { current: "Cigars", proposed: "Menu → Cigars", note: "Learn-More per cigar" },
  { current: "Specials", proposed: "Menu → Specials", note: "Also surfaced under Experience → Events" },
  { current: "Private Dining", proposed: "Private Dining", note: "Rooms, capacities, inquiry form" },
  { current: "Our Story", proposed: "About → Our Story", note: "Editorial redesign" },
  { current: "Dress Code", proposed: "About → Dress Code", note: "" },
  { current: "Hours of Operation", proposed: "About → Hours & Location", note: "Merged into Reservations/About" },
  { current: "FAQ", proposed: "About → FAQ", note: "" },
  { current: "Contact", proposed: "About → Contact", note: "Merged with Reservations info" },
  { current: "Gallery", proposed: "Experience → Gallery", note: "Recommended Phase 2 Content Migration" },
  { current: "Press", proposed: "About → Press", note: "Recommended Phase 2 Content Migration" },
  { current: "Reservations", proposed: "Reservations (primary CTA)", note: "Elevated from nav link to CTA" },
  { current: "Gift Cards", proposed: "Footer", note: "Secondary utility" },
  { current: "Careers / Employment", proposed: "Footer + About → Careers", note: "Secondary utility" },
  { current: "Feedback", proposed: "Footer", note: "Secondary utility" },
  { current: "Mailing List / Email Signup", proposed: "Footer", note: "Secondary utility" },
  { current: "Social Media", proposed: "Footer", note: "Instagram + Facebook" },
];

const findings = [
  "The dinner menu was presented through an embedded third-party view — reimagined as a native, mobile-first HTML menu for better discoverability and performance.",
  "Reservations appeared as an ordinary navigation link — elevated to a prominent, persistent call-to-action to support conversion.",
  "Related utility content (Hours, Contact, FAQ) was distributed across separate pages and has been consolidated into more intuitive customer journeys under About.",
  "Deep utility links (Gift Cards, Feedback, Careers, mailing list) were surfaced in primary navigation — relocated to the footer to declutter the customer path.",
  "Opportunity to strengthen structured restaurant data and semantic SEO — the redesign adds Restaurant schema, semantic markup and local-SEO-friendly content.",
  "Menu content is now CMS-structured so any Pack Brothers property can reuse the system with its own identity.",
];

const SiteAuditPage = () => (
  <main data-testid="site-audit-page" className="bg-[#0a0908] min-h-screen pt-32 pb-24 grain relative">
    <div className="max-w-[1200px] mx-auto px-6 md:px-10">
      <Reveal>
        <p className="text-[10px] tracking-[0.35em] uppercase text-[#c5a059]">Internal · Presentation Only</p>
        <h1 className="font-serif text-4xl md:text-6xl text-white font-light mt-4">Information Architecture Audit</h1>
        <p className="text-white/50 font-light mt-4 max-w-2xl">Current River Stone website → proposed redesign. Demonstrates preserved content with dramatically improved architecture. Not linked in customer navigation.</p>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-14 border border-white/10">
          <div className="grid grid-cols-12 bg-[#161412] text-[10px] tracking-[0.2em] uppercase text-white/50 px-6 py-4">
            <div className="col-span-5">Current</div>
            <div className="col-span-1" />
            <div className="col-span-6">Proposed</div>
          </div>
          {mapping.map((m, i) => (
            <div key={i} className="grid grid-cols-12 items-center px-6 py-4 border-t border-white/10 hover:bg-white/[0.03] transition-colors">
              <div className="col-span-5 text-white/80 text-sm font-light">{m.current}</div>
              <div className="col-span-1 text-[#c5a059]"><ArrowRight size={16} /></div>
              <div className="col-span-6">
                <span className="text-white text-sm">{m.proposed}</span>
                {m.note && <span className="block text-white/40 text-xs font-light mt-0.5">{m.note}</span>}
              </div>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <h2 className="font-serif text-3xl text-white mt-16 mb-6">Key Findings & Opportunities</h2>
        <div className="grid md:grid-cols-2 gap-x-10 gap-y-4">
          {findings.map((f, i) => (
            <div key={i} className="flex gap-3 text-white/60 text-sm font-light leading-relaxed">
              <span className="font-serif text-[#c5a059]">{String(i + 1).padStart(2, "0")}</span>{f}
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </main>
);

export default SiteAuditPage;
